const CartLine = require('@/lib/Cart/CartLine');
const EntityStatus = require('@/lib/Entities/EntityStatus');
const { getItemOptionLinesForLineFromCart } = require('@/lib/ItemOptions/index');

class ItemOptionManager {
    /**
     * @param {ItemOptionRepository} itemOptionRepository
     * @param {OptionSetRepository} optionSetRepository
     * @param {UserService} userService
     */
    constructor(
        itemOptionRepository,
        optionSetRepository,
        userService,
    ) {
        this.optionSetRepository = optionSetRepository;
        this.itemOptionRepository = itemOptionRepository;
        this.userService = userService;
    }

    /**
     * TODO: Cache results?
     *
     * @param itemID
     * @returns {Promise<OptionSet[]>}
     */
    async findOptionSetsForItem(itemID) {
        const sets = await this.optionSetRepository.findActiveForItem(itemID);

        // Attach the options to every set.
        await Promise.all(
            sets.map((set) => this.attachOptionsToSet(set))
        );

        return sets;
    }

    /**
     * Populate the options property of an option set.
     * The set should already have a list of option IDs (integer array) in optionSet.optionIDs.
     * This will set optionSet.options to an array of ItemOption objects.
     *
     * @param {OptionSet} optionSet
     *
     * @returns {Promise}
     */
    async attachOptionsToSet(optionSet) {
        /**
         * @type {ItemOption[]}
         */
        let options = await this.itemOptionRepository.findMany(optionSet.optionIDs);

        // Filter to only active options.
        options = options.filter((option) => option.status === EntityStatus.ACTIVE);

        // Filter to only options for the current item branch.
        options = options.filter((option) => option.branchIDs.includes(this.userService.currentItemBranch.ID));

        // TODO: We need to sort these options by their navOrder on the set to option linkage.
        // But that doesn't exist yet.
        optionSet.options = optionSet.optionIDs
            .map((optionID) => options.find((option) => option.ID === optionID))
            .filter((option) => option); // Filter out any options that were not found.
    }

    /**
     * @param {Cart} cart
     * @param {CartLine} cartLine
     * @param {Object<number, number[]>} selections
     */
    async setItemOptionSelectionsForLine(cart, cartLine, selections) {
        const existingItemOptionLinesForLine = getItemOptionLinesForLineFromCart(cart, cartLine);

        // Remove all existing item option line for this line in the cart.
        // existingItemOptionLinesForLine.forEach((line) => {
        //     // TODO: When adjusting an order we want to keep lines.
        //     cart.removeItem(line);
        // });

        // Ensure there are no extraneous selections for this line in the cart.
        existingItemOptionLinesForLine.forEach((line) => {
            // If this item doesn't appear as a selection, delete the line from the cart.
            if (!selections[line.optionSet.ID] || !selections[line.optionSet.ID].includes(line.getItemID())) {
                cart.removeItem(line);
            }
        });

        // Add any selections that are not already in the cart.
        // For each option set that has any selections...
        for (let optionSetID of Object.keys(selections)) {
            // Load the OptionSet from IndexedDB.
            const optionSet = await this.optionSetRepository.find(optionSetID);
            optionSetID = optionSet.ID; // Ensure we have the correct ID type (number).

            // For each selected item option in this set...
            const itemOptionIDs = selections[optionSetID];
            for (const itemOptionID of itemOptionIDs) {
                // Check if there is already an item option line for this set for this selection.
                console.log('Checking for existing item option line for set', optionSetID, 'and option', itemOptionID);
                let itemOptionLine = existingItemOptionLinesForLine.find((line) => {
                    console.log('Comparing against line', line.optionSet?.ID, line.getItemID(), line.optionSet?.ID === optionSetID, line.getItemID() === itemOptionID);
                    return line.optionSet?.ID === optionSetID && line.getItemID() === itemOptionID;
                });

                if (!itemOptionLine) {
                    // Add a new line to the cart for this selection.

                    // Load the ItemOption from IndexedDB.
                    const itemOption = await this.itemOptionRepository.find(itemOptionID);

                    // Create the ItemOption line.
                    itemOptionLine = new CartLine(null, itemOption, cartLine.branch);
                    itemOptionLine.parentThirdPartyRef = cartLine.thirdPartyRef;
                    itemOptionLine.setCanAdjustQuantity(false);
                    itemOptionLine.setCanRemove(false);
                    itemOptionLine.optionSet = optionSet;

                    await cart.addItem(itemOptionLine);
                }

                // Always update the quantity of the item option line to match the cart line's quantity.
                itemOptionLine.setQuantity(cartLine.quantity);
            }
        }
    }

    /**
     * @param {OrderLine[]} orderLines
     * @returns {Promise<Awaited<unknown>[]>}
     */
    attachOptionSetsToOrderLines(orderLines) {
        return Promise.allSettled(orderLines.map(async (line) => {
            if (line.optionSetID) {
                line.optionSet = await this.optionSetRepository.find(line.optionSetID);
            }
            return line;
        }));
    }
}

module.exports = ItemOptionManager;
